// =============================================================================
// Origin UI — src/pages/evaluations/EvaluationReportsPage.tsx
// Evaluation reports — 6 tabs: Distribution, Department, Calibration,
// Completion, Detail, Fairness.
// Phase 7, Task 7.6 — Year-End Evaluation & Probation Review module.
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { evaluationApi } from '../../services/evaluationApi';
import { referenceApi } from '../../services/requestApi';
import type {
  EvaluationCycleListItem,
  RatingDistribution,
  PerformanceTrend,
  DepartmentBreakdown,
  ManagerCalibration,
  CompletionTracker,
  FairnessDashboard,
  EvaluationListItem,
  BiasIndicator,
} from '../../services/evaluationApi';
import {
  generateDistributionPdf,
  generateDepartmentPdf,
  generateCalibrationPdf,
  generateCompletionPdf,
  generateFairnessPdf,
} from '../../services/reportPdfGenerator';
import styles from './evaluations.module.css';

// ─── Rating colour map ───
const RATING_COLOURS: Record<number, string> = {
  5: '#0A7B4F', // Outstanding — deep green
  4: '#2AAA6A', // Exceeds — green
  3: '#FF8300', // Meets — OQT orange
  2: '#E06020', // Needs improvement — dark orange
  1: '#C92A2A', // Unacceptable — red
};

const ratingColour = (value: number) => RATING_COLOURS[value] || '#888';

// ─── Shared components ───

function LoadingSpinner() {
  return <div className={styles.loadingBox}>Loading report data…</div>;
}

function EmptyState({ message }: { message: string }) {
  return <div className={styles.emptyState}>{message}</div>;
}

function CycleSelector({
  cycles,
  selectedId,
  onChange,
}: {
  cycles: EvaluationCycleListItem[];
  selectedId: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className={styles.filterRow}>
      <label className={styles.filterLabel}>Cycle</label>
      <select
        className={styles.filterSelect}
        value={selectedId}
        onChange={e => onChange(e.target.value)}
      >
        {cycles.map(c => (
          <option key={c.evaluationCycleId} value={c.evaluationCycleId}>
            {c.name} ({c.reviewYear})
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── SVG Bar Chart ───

function BarChart({
  items,
  maxValue,
  labelKey,
  valueKey,
  colourFn,
  suffix = '',
  height = 220,
}: {
  items: any[];
  maxValue: number;
  labelKey: string;
  valueKey: string;
  colourFn: (item: any) => string;
  suffix?: string;
  height?: number;
}) {
  if (items.length === 0) return <EmptyState message="No data available." />;
  const barWidth = Math.min(60, Math.floor(500 / items.length));
  const gap = 8;
  const chartWidth = items.length * (barWidth + gap) + 40;
  const chartH = height;
  const topPad = 24;
  const bottomPad = 50;
  const drawH = chartH - topPad - bottomPad;

  return (
    <svg viewBox={`0 0 ${chartWidth} ${chartH}`} style={{ width: '100%', maxWidth: chartWidth, height: 'auto' }}>
      {items.map((item, i) => {
        const val = item[valueKey] || 0;
        const barH = maxValue > 0 ? (val / maxValue) * drawH : 0;
        const x = 30 + i * (barWidth + gap);
        const y = topPad + drawH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barWidth} height={barH} rx={3} fill={colourFn(item)} />
            <text x={x + barWidth / 2} y={y - 6} textAnchor="middle" fontSize="11" fill="#333" fontWeight="600">
              {val}{suffix}
            </text>
            <text x={x + barWidth / 2} y={chartH - bottomPad + 16} textAnchor="middle" fontSize="10" fill="#666">
              {(item[labelKey] || '').length > 12 ? (item[labelKey] || '').substring(0, 12) + '…' : item[labelKey]}
            </text>
          </g>
        );
      })}
      {/* Y-axis line */}
      <line x1="28" y1={topPad} x2="28" y2={topPad + drawH} stroke="#ddd" strokeWidth="1" />
      {/* X-axis line */}
      <line x1="28" y1={topPad + drawH} x2={chartWidth} y2={topPad + drawH} stroke="#ddd" strokeWidth="1" />
    </svg>
  );
}

// ─── SVG Donut Chart ───

function DonutChart({ value, total, label, colour }: { value: number; total: number; label: string; colour: string }) {
  const pct = total > 0 ? value / total : 0;
  const r = 36;
  const circ = 2 * Math.PI * r;
  const filled = circ * pct;
  return (
    <div style={{ textAlign: 'center', minWidth: 140 }}>
      <svg viewBox="0 0 100 100" style={{ width: 110, height: 110, display: 'block', margin: '0 auto' }}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#eee" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={r} fill="none" stroke={colour} strokeWidth="8"
          strokeDasharray={`${filled} ${circ - filled}`}
          strokeDashoffset={circ / 4}
          strokeLinecap="round"
        />
        <text x="50" y="46" textAnchor="middle" fontSize="18" fontWeight="700" fill="#0A2240">
          {Math.round(pct * 100)}%
        </text>
        <text x="50" y="60" textAnchor="middle" fontSize="10" fill="#666">
          {value}/{total}
        </text>
      </svg>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0A2240', marginTop: 6 }}>{label}</div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function EvaluationReportsPage() {
  const [tab, setTab] = useState('distribution');
  const [cycles, setCycles] = useState<EvaluationCycleListItem[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState<string[]>([]);
  const [offices, setOffices] = useState<string[]>([]);

  // Load reference data
  useEffect(() => {
    (async () => {
      try {
        const [depts, offs] = await Promise.all([referenceApi.departments(), referenceApi.officeLocations()]);
        setDepartments(depts.map((d: any) => d.name).sort());
        setOffices(offs.map((o: any) => o.name).sort());
      } catch {}
    })();
  }, []);
  // Load cycles on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await evaluationApi.getCycles();
        const nonDraft = data.filter((c: EvaluationCycleListItem) => c.status !== 'Draft');
        setCycles(nonDraft);
        if (nonDraft.length > 0) setSelectedCycleId(nonDraft[0].evaluationCycleId);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className={styles.page}><LoadingSpinner /></div>;

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Evaluation Reports</h1>

      <div className={styles.tabBar}>
        {[
          ['distribution', 'Rating Distribution'],
          ['department', 'Department Breakdown'],
          ['calibration', 'Manager Calibration'],
          ['completion', 'Completion Tracker'],
          ['detail', 'Individual Detail'],
          ['fairness', 'Fairness Dashboard'],
        ].map(([key, label]) => (
          <button
            key={key}
            className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {cycles.length === 0 ? (
        <div className={styles.card}><EmptyState message="No evaluation cycles found." /></div>
      ) : (
        <>
          {tab === 'distribution' && <DistributionTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} departments={departments} offices={offices} />}
          {tab === 'department' && <DepartmentTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} />}
          {tab === 'calibration' && <CalibrationTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} />}
          {tab === 'completion' && <CompletionTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} />}
          {tab === 'detail' && <DetailTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} departments={departments} offices={offices} />}
          {tab === 'fairness' && <FairnessTab cycleId={selectedCycleId} cycles={cycles} onCycleChange={setSelectedCycleId} departments={departments} />}
        </>
      )}
    </div>
  );
}

// =============================================================================
// TAB 1: Rating Distribution & Trends
// =============================================================================

function DistributionTab({ cycleId, cycles, onCycleChange, departments, offices }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void; departments: string[]; offices: string[] }) {
  const [distribution, setDistribution] = useState<RatingDistribution[]>([]);
  const [trends, setTrends] = useState<PerformanceTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [dist, trendData] = await Promise.all([
          evaluationApi.getRatingDistribution(cycleId, deptFilter || undefined, officeFilter || undefined),
          evaluationApi.getPerformanceTrends(deptFilter || undefined, officeFilter || undefined),
        ]);
        if (!cancelled) { setDistribution(dist); setTrends(trendData); }
      } catch { if (!cancelled) { setDistribution([]); setTrends([]); } }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cycleId, deptFilter, officeFilter]);

  const maxCount = useMemo(() => Math.max(...distribution.map(d => d.count), 1), [distribution]);
  const avgRating = useMemo(() => {
    if (distribution.length === 0) return 0;
    const total = distribution.reduce((sum, d) => sum + d.numericValue * d.count, 0);
    const count = distribution.reduce((sum, d) => sum + d.count, 0);
    return count > 0 ? (total / count).toFixed(2) : '0';
  }, [distribution]);

  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Department</label>
          <select className={styles.filterInput} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
        </div>
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Office</label>
          <select className={styles.filterInput} value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                <option value="">All Offices</option>
                {offices.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
        </div>
        <button className={styles.pdfBtn} onClick={() => { const cn = cycles.find(c => c.evaluationCycleId === cycleId)?.name || ""; generateDistributionPdf(distribution, trends, cn, { department: deptFilter, office: officeFilter }); }}>Export PDF</button>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div id="report-distribution">
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Overall Rating Distribution</h3>
            {distribution.length === 0 ? <EmptyState message="No completed evaluations with ratings yet." /> : (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Average Rating:</span>
                  <span className={styles.statValue}>{avgRating}</span>
                  <span className={styles.statLabel} style={{ marginLeft: 24 }}>Total Rated:</span>
                  <span className={styles.statValue}>{distribution.reduce((s, d) => s + d.count, 0)}</span>
                </div>
                <BarChart
                  items={distribution}
                  maxValue={maxCount}
                  labelKey="ratingLabel"
                  valueKey="count"
                  colourFn={item => ratingColour(item.numericValue)}
                />
                <div className={styles.percentageRow}>
                  {distribution.map(d => (
                    <span key={d.numericValue} className={styles.percentageChip} style={{ borderLeft: `3px solid ${ratingColour(d.numericValue)}` }}>
                      {d.ratingLabel}: {d.percentage}%
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {trends.length > 0 && (
            <div className={styles.card} style={{ marginTop: 16 }}>
              <h3 className={styles.cardTitle}>Year-over-Year Trends</h3>
              <BarChart
                items={trends}
                maxValue={5}
                labelKey="cycleName"
                valueKey="averageRating"
                colourFn={() => '#00B0B9'}
                height={180}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB 2: Department Breakdown
// =============================================================================

function DepartmentTab({ cycleId, cycles, onCycleChange }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void }) {
  const [data, setData] = useState<DepartmentBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [dSortCol, setDSortCol] = useState<string>('department');
  const [dSortDir, setDSortDir] = useState<'asc' | 'desc'>('asc');
  const dToggle = (col: string) => { if (dSortCol === col) setDSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setDSortCol(col); setDSortDir('asc'); } };
  const dIcon = (col: string) => dSortCol === col ? (dSortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const dSorted = [...data].sort((a: any, b: any) => { const av = a[dSortCol] ?? ''; const bv = b[dSortCol] ?? ''; if (av < bv) return dSortDir === 'asc' ? -1 : 1; if (av > bv) return dSortDir === 'asc' ? 1 : -1; return 0; });

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const result = await evaluationApi.getDepartmentBreakdown(cycleId); if (!cancelled) setData(result); }
      catch { if (!cancelled) setData([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cycleId]);


  const maxAvg = useMemo(() => 5, []);
  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <button className={styles.pdfBtn} onClick={() => { const cn = cycles.find(c => c.evaluationCycleId === cycleId)?.name || ""; generateDepartmentPdf(data, cn); }}>Export PDF</button>
      </div>

      {loading ? <LoadingSpinner /> : data.length === 0 ? (
        <div className={styles.card}><EmptyState message="No department data available." /></div>
      ) : (
        <div id="report-department">
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Average Rating by Department</h3>
            <BarChart
              items={data}
              maxValue={maxAvg}
              labelKey="department"
              valueKey="averageRating"
              colourFn={item => {
                const v = Math.round(item.averageRating);
                return ratingColour(v);
              }}
            />
          </div>

          <div className={styles.card} style={{ marginTop: 16 }}>
            <h3 className={styles.cardTitle}>Department Detail</h3>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th onClick={() => dToggle('department')} style={{ cursor: 'pointer' }}>Department{dIcon('department')}</th>
                  <th onClick={() => dToggle('count')} style={{ cursor: 'pointer' }}>Employees{dIcon('count')}</th>
                  <th onClick={() => dToggle('avgRating')} style={{ cursor: 'pointer' }}>Avg Rating{dIcon('avgRating')}</th>
                  <th>Outstanding</th>
                  <th>Exceeds</th>
                  <th>Meets</th>
                  <th>Needs Improv.</th>
                  <th>Unacceptable</th>
                </tr>
              </thead>
              <tbody>
                {dSorted.map(d => {
                  const findDist = (val: number) => d.distribution.find(x => x.numericValue === val);
                  return (
                    <tr key={d.department}>
                      <td style={{ fontWeight: 600 }}>{d.department}</td>
                      <td>{d.employeeCount}</td>
                      <td>
                        <span className={styles.avgBadge} style={{ background: ratingColour(Math.round(d.averageRating)) }}>
                          {d.averageRating}
                        </span>
                      </td>
                      <td>{findDist(5)?.count || 0}</td>
                      <td>{findDist(4)?.count || 0}</td>
                      <td>{findDist(3)?.count || 0}</td>
                      <td>{findDist(2)?.count || 0}</td>
                      <td>{findDist(1)?.count || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB 3: Manager Calibration & Bias Detection
// =============================================================================

function CalibrationTab({ cycleId, cycles, onCycleChange }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void }) {
  const [data, setData] = useState<ManagerCalibration[]>([]);
  const [loading, setLoading] = useState(true);
  const [cSortCol, setCSortCol] = useState<string>('managerName');
  const [cSortDir, setCSortDir] = useState<'asc' | 'desc'>('asc');
  const cToggle = (col: string) => { if (cSortCol === col) setCSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setCSortCol(col); setCSortDir('asc'); } };
  const cIcon = (col: string) => cSortCol === col ? (cSortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const cSorted = [...data].sort((a: any, b: any) => { const av = a[cSortCol] ?? ''; const bv = b[cSortCol] ?? ''; if (av < bv) return cSortDir === 'asc' ? -1 : 1; if (av > bv) return cSortDir === 'asc' ? 1 : -1; return 0; });

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const result = await evaluationApi.getManagerCalibration(cycleId); if (!cancelled) setData(result); }
      catch { if (!cancelled) setData([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cycleId]);

  const flagColour = (flag: string) => {
    switch (flag) {
      case 'VeryLenient': return '#C92A2A';
      case 'Lenient': return '#E06020';
      case 'VeryStrict': return '#C92A2A';
      case 'Strict': return '#E06020';
      default: return '#0A7B4F';
    }
  };

  const flagLabel = (flag: string) => {
    switch (flag) {
      case 'VeryLenient': return '⬆ Very Lenient';
      case 'Lenient': return '⬆ Lenient';
      case 'VeryStrict': return '⬇ Very Strict';
      case 'Strict': return '⬇ Strict';
      default: return '✓ Normal';
    }
  };

  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <button className={styles.pdfBtn} onClick={() => { const cn = cycles.find(c => c.evaluationCycleId === cycleId)?.name || ""; generateCalibrationPdf(data, cn); }}>Export PDF</button>
      </div>

      {loading ? <LoadingSpinner /> : data.length === 0 ? (
        <div className={styles.card}><EmptyState message="No calibration data available." /></div>
      ) : (
        <div id="report-calibration">
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Manager Calibration</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Compares each manager's average rating against the company average. Flags managers who deviate significantly (±0.4 points).
            </p>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th onClick={() => cToggle('managerName')} style={{ cursor: 'pointer' }}>Manager{cIcon('managerName')}</th>
                  <th onClick={() => cToggle('reviewCount')} style={{ cursor: 'pointer' }}>Reviews{cIcon('reviewCount')}</th>
                  <th onClick={() => cToggle('avgRating')} style={{ cursor: 'pointer' }}>Avg Rating{cIcon('avgRating')}</th>
                  <th onClick={() => cToggle('companyAvg')} style={{ cursor: 'pointer' }}>Company Avg{cIcon('companyAvg')}</th>
                  <th onClick={() => cToggle('deviation')} style={{ cursor: 'pointer' }}>Deviation{cIcon('deviation')}</th>
                  <th onClick={() => cToggle('flag')} style={{ cursor: 'pointer' }}>Flag{cIcon('flag')}</th>
                </tr>
              </thead>
              <tbody>
                {cSorted.map(m => (
                  <tr key={m.managerId}>
                    <td style={{ fontWeight: 600 }}>{m.managerName}</td>
                    <td>{m.reviewedCount}</td>
                    <td>{m.averageRating}</td>
                    <td>{m.companyAverage}</td>
                    <td style={{ color: m.deviation > 0 ? '#2AAA6A' : m.deviation < 0 ? '#C92A2A' : '#333' }}>
                      {m.deviation > 0 ? '+' : ''}{m.deviation}
                    </td>
                    <td>
                      <span className={styles.flagBadge} style={{ color: flagColour(m.deviationFlag), borderColor: flagColour(m.deviationFlag) }}>
                        {flagLabel(m.deviationFlag)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bias Indicators */}
          {data.some(m => m.biasIndicators && m.biasIndicators.length > 0) && (
            <div className={styles.card} style={{ marginTop: 16 }}>
              <h3 className={styles.cardTitle}>Bias Detection Flags</h3>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                Flags where a manager rates one demographic group ≥0.5 points differently from another. This is an indicator for review, not a definitive finding.
              </p>
              {data.filter(m => m.biasIndicators && m.biasIndicators.length > 0).map(m => (
                <div key={m.managerId} style={{ marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#0A2240', marginBottom: 6 }}>{m.managerName}</h4>
                  {m.biasIndicators!.map((b: BiasIndicator, bi: number) => (
                    <div key={bi} className={styles.biasFlagRow}>
                      <span className={styles.biasDimension}>{b.dimension}</span>
                      <span>{b.groupA} ({b.groupAAverage}) vs {b.groupB} ({b.groupBAverage})</span>
                      <span className={styles.biasDiff} style={{ color: b.isFlagged ? '#C92A2A' : '#666' }}>
                        Δ {b.difference} {b.isFlagged ? '⚠' : ''}
                      </span>
                      {b.note && <span className={styles.biasNote}>{b.note}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB 4: Completion Tracker
// =============================================================================

function CompletionTab({ cycleId, cycles, onCycleChange }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void }) {
  const [data, setData] = useState<CompletionTracker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tSortCol, setTSortCol] = useState<string>('department');
  const [tSortDir, setTSortDir] = useState<'asc' | 'desc'>('asc');
  const tToggle = (col: string) => { if (tSortCol === col) setTSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setTSortCol(col); setTSortDir('asc'); } };
  const tIcon = (col: string) => tSortCol === col ? (tSortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await evaluationApi.getCompletionTracker(cycleId);
        if (!cancelled) setData(result);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load completion data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cycleId]);
  const selectedCycle = cycles.find(c => c.evaluationCycleId === cycleId);
  const isMidYear = selectedCycle?.cycleType === 'MidYearReview';

  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <button className={styles.pdfBtn} onClick={() => { if (data) { const cn = cycles.find(c => c.evaluationCycleId === cycleId)?.name || ""; generateCompletionPdf(data, cn); } }}>Export PDF</button>
      </div>

      {loading ? <LoadingSpinner /> : error ? (
        <div className={styles.card}><EmptyState message={error} /></div>
      ) : !data ? (
        <div className={styles.card}><EmptyState message="No completion data available." /></div>
      ) : (
        <div id="report-completion">
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Completion Progress</h3>
            <div className={styles.donutRow}>
              {!isMidYear && <DonutChart value={data.employeeSubmitted} total={data.totalEligible} label="Employee Submitted" colour="#FF8300" />}
              <DonutChart value={data.managerCompleted} total={data.totalEligible} label="Completed by Manager" colour="#00B0B9" />
              <DonutChart value={data.acknowledged} total={data.totalEligible} label="Acknowledged by Employee" colour="#0A7B4F" />
            </div>
          </div>

          <div className={styles.card} style={{ marginTop: 16 }}>
            <h3 className={styles.cardTitle}>By Department</h3>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th onClick={() => tToggle('department')} style={{ cursor: 'pointer' }}>Department{tIcon('department')}</th>
                  <th onClick={() => tToggle('total')} style={{ cursor: 'pointer' }}>Total{tIcon('total')}</th>
                  {!isMidYear && <th>Submitted</th>}
                  <th onClick={() => tToggle('completed')} style={{ cursor: 'pointer' }}>Completed by Manager{tIcon('completed')}</th>
                  <th onClick={() => tToggle('acknowledged')} style={{ cursor: 'pointer' }}>Acknowledged by Employee{tIcon('acknowledged')}</th>
                  <th onClick={() => tToggle('progress')} style={{ cursor: 'pointer' }}>Progress{tIcon('progress')}</th>
                </tr>
              </thead>
              <tbody>
                {data.byDepartment.map(d => {
                  const pct = d.total > 0 ? Math.round((d.acknowledged / d.total) * 100) : 0;
                  return (
                    <tr key={d.department}>
                      <td style={{ fontWeight: 600 }}>{d.department}</td>
                      <td>{d.total}</td>
                      {!isMidYear && <td>{d.submitted}</td>}
                      <td>{d.completed}</td>
                      <td>{d.acknowledged}</td>
                      <td>
                        <div className={styles.reportProgressBar}>
                          <div className={styles.reportProgressFill} style={{ width: `${pct}%` }} />
                        </div>
                        <span className={styles.reportProgressLabel}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data.overdue.length > 0 && (
            <div className={styles.card} style={{ marginTop: 16 }}>
              <h3 className={styles.cardTitle} style={{ color: '#C92A2A' }}>
                Overdue ({data.overdue.length})
              </h3>
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Pending Action</th>
                    <th>Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.overdue.map((o, i) => (
                    <tr key={i}>
                      <td>{o.employeeName}</td>
                      <td>{o.department}</td>
                      <td>
                        <span className={styles.actionBadge}>
                          {o.pendingAction === 'EmployeeSubmission' ? 'Self-Assessment' : 'Manager Review'}
                        </span>
                      </td>
                      <td style={{ color: '#C92A2A', fontWeight: 600 }}>{o.daysOverdue} days</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// TAB 5: Individual Detail
// =============================================================================

function DetailTab({ cycleId, cycles, onCycleChange, departments, offices }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void; departments: string[]; offices: string[] }) {
  const [data, setData] = useState<EvaluationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [sortCol, setSortCol] = useState<string>('employeeName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const result = await evaluationApi.getDetailReport(cycleId, deptFilter || undefined, officeFilter || undefined); if (!cancelled) setData(result); }
      catch { if (!cancelled) setData([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cycleId, deptFilter, officeFilter]);

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a: any, b: any) => {
      const av = a[sortCol] || '';
      const bv = b[sortCol] || '';
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [data, sortCol, sortDir]);

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const exportCsv = () => {
    window.location.href = `/api/evaluations/reports/detail/export?cycleId=${cycleId}${deptFilter ? `&department=${encodeURIComponent(deptFilter)}` : ''}${officeFilter ? `&office=${encodeURIComponent(officeFilter)}` : ''}`;
  };

  const sortIcon = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Department</label>
          <select className={styles.filterInput} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
        </div>
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Office</label>
          <select className={styles.filterInput} value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                <option value="">All Offices</option>
                {offices.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
        </div>
        <button className={styles.exportBtn} onClick={exportCsv}>⬇ Export CSV</button>
      </div>

      {loading ? <LoadingSpinner /> : data.length === 0 ? (
        <div className={styles.card}><EmptyState message="No evaluations found." /></div>
      ) : (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>All Evaluations ({data.length})</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th onClick={() => toggleSort('employeeName')} style={{ cursor: 'pointer' }}>Employee{sortIcon('employeeName')}</th>
                  <th onClick={() => toggleSort('employeeNumber')} style={{ cursor: 'pointer' }}>ID{sortIcon('employeeNumber')}</th>
                  <th onClick={() => toggleSort('department')} style={{ cursor: 'pointer' }}>Department{sortIcon('department')}</th>
                  <th onClick={() => toggleSort('office')} style={{ cursor: 'pointer' }}>Office{sortIcon('office')}</th>
                  <th onClick={() => toggleSort('managerName')} style={{ cursor: 'pointer' }}>Manager{sortIcon('managerName')}</th>
                  <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer' }}>Status{sortIcon('status')}</th>
                  <th onClick={() => toggleSort('overallRating')} style={{ cursor: 'pointer' }}>Rating{sortIcon('overallRating')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(e => (
                  <tr key={e.evaluationId}>
                    <td style={{ fontWeight: 600 }}>{e.employeeName}</td>
                    <td>{e.employeeNumber}</td>
                    <td>{e.department}</td>
                    <td>{e.office}</td>
                    <td>{e.managerName}</td>
                    <td><span className={`${styles.statusPill} ${styles['status_' + e.status]}`}>{formatStatus(e.status)}</span></td>
                    <td>{e.overallRating || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case 'NotStarted': return 'Not Started';
    case 'EmployeeDraft': return 'Draft';
    case 'EmployeeSubmitted': return 'Submitted';
    case 'ManagerDraft': return 'In Review';
    case 'ManagerCompleted': return 'Completed';
    case 'Acknowledged': return 'Acknowledged';
    default: return status;
  }
}

// =============================================================================
// TAB 6: Fairness Dashboard
// =============================================================================

function FairnessTab({ cycleId, cycles, onCycleChange, departments }: { cycleId: string; cycles: EvaluationCycleListItem[]; onCycleChange: (id: string) => void; departments: string[] }) {
  const [data, setData] = useState<FairnessDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('');

  useEffect(() => {
    if (!cycleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const result = await evaluationApi.getFairnessDashboard(cycleId, deptFilter || undefined); if (!cancelled) setData(result); }
      catch { if (!cancelled) setData(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [cycleId, deptFilter]);

  return (
    <div>
      <div className={styles.filterBar}>
        <CycleSelector cycles={cycles} selectedId={cycleId} onChange={onCycleChange} />
        <div className={styles.filterRow}>
          <label className={styles.filterLabel}>Department</label>
          <select className={styles.filterInput} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                <option value="">All Departments</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
        </div>
        <button className={styles.pdfBtn} onClick={() => { if (data) { const cn = cycles.find(c => c.evaluationCycleId === cycleId)?.name || ""; generateFairnessPdf(data, cn, deptFilter); } }}>Export PDF</button>
      </div>

      {loading ? <LoadingSpinner /> : !data ? (
        <div className={styles.card}><EmptyState message="No fairness data available." /></div>
      ) : (
        <div id="report-fairness">
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>About This Dashboard</h3>
            <p style={{ fontSize: 13, color: '#555', lineHeight: 1.6, margin: 0 }}>
              The Fairness Dashboard helps HR identify potential patterns or disparities in how performance ratings are distributed across different employee groups. 
              It is not a definitive finding of bias — it highlights areas that may warrant further review. Groups with fewer than 5 employees are marked as 
              "insufficient data" to prevent misleading conclusions from small samples. A difference of ≥0.5 points between groups is flagged for attention.
            </p>
          </div>

          {/* By Gender */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <h3 className={styles.cardTitle}>Rating by Gender</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Compares average performance ratings between gender groups. Large differences may indicate unconscious bias in how managers rate employees of different genders.
            </p>
            {data.byGender.length === 0 ? <EmptyState message="No gender data." /> : (
              <div className={styles.fairnessGrid}>
                {data.byGender.map(g => (
                  <FairnessGroupCard key={g.groupName} group={g} />
                ))}
              </div>
            )}
          </div>

          {/* By Tenure Band */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <h3 className={styles.cardTitle}>Rating by Tenure</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Shows how ratings vary by length of service. Newer employees may receive lower ratings as they are still developing, while long-tenured employees may cluster higher. Large gaps between bands could indicate that tenure is being over- or under-weighted in evaluations.
            </p>
            {data.byTenureBand.length === 0 ? <EmptyState message="No tenure data." /> : (
              <div className={styles.fairnessGrid}>
                {data.byTenureBand.map(g => (
                  <FairnessGroupCard key={g.groupName} group={g} />
                ))}
              </div>
            )}
          </div>

          {/* By Office */}
          <div className={styles.card} style={{ marginTop: 16 }}>
            <h3 className={styles.cardTitle}>Rating by Office</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Compares average ratings across office locations. Significant differences might reflect genuine performance variations, different management styles at each office, or cultural differences in how ratings are applied.
            </p>
            {data.byOffice.length === 0 ? <EmptyState message="No office data." /> : (
              <div className={styles.fairnessGrid}>
                {data.byOffice.map(g => (
                  <FairnessGroupCard key={g.groupName} group={g} />
                ))}
              </div>
            )}
          </div>

          {/* Intersectional Heatmap */}
          {data.intersectionalHeatmap.length > 0 && (
            <div className={styles.card} style={{ marginTop: 16 }}>
              <h3 className={styles.cardTitle}>Intersectional Heatmap (Gender × Department)</h3>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                Cross-references gender and department to reveal patterns not visible in single-dimension analysis. For example, a company may show no overall gender gap, but a specific department might rate one gender significantly differently. Green cells indicate higher averages, yellow is mid-range, red is lower. Grey cells have insufficient data.
              </p>
              <HeatmapTable cells={data.intersectionalHeatmap} />
            </div>
          )}

          {/* Statistical Flags */}
          {data.statisticalFlags.length > 0 && (
            <div className={styles.card} style={{ marginTop: 16 }}>
              <h3 className={styles.cardTitle}>Statistical Flags</h3>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                Specific comparisons where the difference between groups exceeds the 0.5-point threshold. These are worth investigating but are not proof of bias — small sample sizes and legitimate performance differences can produce these flags.
              </p>
              {data.statisticalFlags.map((f, i) => (
                <div key={i} className={styles.biasFlagRow}>
                  <span className={styles.biasDimension}>{f.dimension}</span>
                  <span>{f.groupA} ({f.groupAAverage}) vs {f.groupB} ({f.groupBAverage})</span>
                  <span className={styles.biasDiff} style={{ color: f.isFlagged ? '#C92A2A' : '#666' }}>
                    Δ {f.difference} {f.isFlagged ? '⚠' : ''}
                  </span>
                  {f.note && <span className={styles.biasNote}>{f.note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FairnessGroupCard({ group }: { group: { groupName: string; count: number; averageRating: number; insufficientData: boolean; distribution: RatingDistribution[] } }) {
  return (
    <div className={styles.fairnessCard}>
      <div className={styles.fairnessCardHeader}>
        <span className={styles.fairnessGroupName}>{group.groupName}</span>
        <span className={styles.fairnessCount}>{group.count} employees</span>
      </div>
      {group.insufficientData ? (
        <p className={styles.insufficientData}>Insufficient data (min 5 required)</p>
      ) : (
        <>
          <div className={styles.fairnessAvg}>
            <span className={styles.avgBadge} style={{ background: ratingColour(Math.round(group.averageRating)) }}>
              {group.averageRating}
            </span>
            <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>avg rating</span>
          </div>
          <div className={styles.miniDistribution}>
            {group.distribution.map(d => (
              <div key={d.numericValue} className={styles.miniDistBar}>
                <div
                  className={styles.miniDistFill}
                  style={{ width: `${d.percentage}%`, background: ratingColour(d.numericValue) }}
                />
                <span className={styles.miniDistLabel}>{d.percentage}%</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HeatmapTable({ cells }: { cells: { gender: string; department: string; count: number; averageRating: number; insufficientData: boolean }[] }) {
  const departments = [...new Set(cells.map(c => c.department))].sort();
  const genders = [...new Set(cells.map(c => c.gender))].sort();

  const getCellData = (gender: string, dept: string) =>
    cells.find(c => c.gender === gender && c.department === dept);

  const heatColour = (avg: number, insufficient: boolean) => {
    if (insufficient) return '#f5f5f5';
    if (avg >= 4) return '#d4edda';
    if (avg >= 3) return '#fff3cd';
    if (avg >= 2) return '#f8d7da';
    return '#f5c6cb';
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className={styles.reportTable}>
        <thead>
          <tr>
            <th>Gender</th>
            {departments.map(d => <th key={d}>{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {genders.map(g => (
            <tr key={g}>
              <td style={{ fontWeight: 600 }}>{g}</td>
              {departments.map(d => {
                const cell = getCellData(g, d);
                if (!cell) return <td key={d} style={{ background: '#f5f5f5', color: '#aaa', textAlign: 'center' }}>—</td>;
                return (
                  <td key={d} style={{ background: heatColour(cell.averageRating, cell.insufficientData), textAlign: 'center' }}>
                    {cell.insufficientData ? (
                      <span style={{ color: '#aaa', fontSize: 11 }}>n={cell.count}</span>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{cell.averageRating} <span style={{ fontSize: 10, color: '#666' }}>({cell.count})</span></span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
